// Renders a contact's mercury avatar to a PNG data URL for use as a
// notification icon (the personalized hidden-tab ring, hiddenRing.ts). This is
// page-side only — it needs a canvas — and the seed is resolved LOCALLY from
// the cached friends list, so a caller's identity never travels through push
// infrastructure (the push payload stays {type, callId}); it only enriches a
// notification the page itself raises while a tab is open.
//
// Uses the frozen mercury-avatar-v1 renderer (avatar/mercury.ts) — the same
// single draw path as everywhere else; never a second renderer.

import { candidateSeed, render } from '../avatar/mercury.js';

/**
 * A data: URL of the peer's avatar disc, or undefined when it can't be produced
 * (no canvas / off-DOM). Sized for a notification icon; `circle` mask so it
 * reads as the same disc shown in-app.
 */
export function avatarNotificationIcon(
  peerUserId: string,
  avatarSeed: number,
  sizePx = 96,
): string | undefined {
  try {
    const px = Math.max(1, Math.round(sizePx));
    const rgba = render(candidateSeed(peerUserId, avatarSeed), px, 'circle');
    const canvas = document.createElement('canvas');
    canvas.width = px;
    canvas.height = px;
    const ctx = canvas.getContext('2d');
    if (!ctx) return undefined;
    ctx.putImageData(new ImageData(rgba, px, px), 0, 0);
    return canvas.toDataURL('image/png');
  } catch {
    return undefined;
  }
}
